import EditIcon from '@mui/icons-material/Edit';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuProps,
  Tooltip,
  useTheme,
} from '@mui/material';
import { BaseMiniMenuProps } from 'components/Minimenus/MiniMenuSelector';
import { getMiniMenuIconSx, miniMenuSx } from 'components/Minimenus/miniMenuStyles';
import type { GenericSearchItem } from 'components/Search/components/SearchItemsProvider';
import type { BroadcastMessageItem } from 'providers/contexts/extensions/Broadcast';
import { useCosmos } from 'providers/contexts/CosmosContext';
import React, { forwardRef, memo, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

export type MuiCloseEvent = Partial<Parameters<NonNullable<MenuProps['onClose']>>[0]>;
export type MuiCloseReasons = Partial<Parameters<NonNullable<MenuProps['onClose']>>[1]>;

export interface NotifierMiniMenuConfig {
  edit?: { enabled?: boolean };
  viewLogs?: { enabled?: boolean };
}

export const allNotifierMenuOptions: NotifierMiniMenuConfig = {
  edit: { enabled: true },
  viewLogs: { enabled: true },
};

export interface NotifierMiniMenuProps extends Omit<
  BaseMiniMenuProps,
  'selectedItems' | 'contextMenuItem'
> {
  selectedItems: GenericSearchItem[];
  menuConfig?: NotifierMiniMenuConfig;
  contextMenuItem?: GenericSearchItem | null;
}

type ButtonState = {
  show: boolean;
  disabled: boolean;
  info?: string;
};

const LOGGABLE_STATUSES = new Set<string>([
  'sent',
  'failed',
  'incomplete',
  'undelivered',
]);

const READ_ONLY_STATUSES = new Set<string>(['sent']);

export const NotifierMiniMenu = memo(
  forwardRef<HTMLDivElement, NotifierMiniMenuProps>(
    (
      {
        menuEvent,
        handleMiniMenuClose,
        menuConfig: menuConfigProp = allNotifierMenuOptions,
        contextMenuItem,
        anchorEl,
      },
      ref,
    ) => {
      const theme = useTheme();
      const { t } = useTranslation();
      const [searchParams, setSearchParams] = useSearchParams();
      const { userInfo } = useCosmos();

      // Delivery logs are a staff-only API; hide the entry point for everyone else.
      const isStaffUser = userInfo?.account_type === 'staff';

      const menuConfig = useMemo(() => menuConfigProp, [menuConfigProp]);

      // Determine menu mode: button (anchorEl) or context (menuEvent position)
      const isButtonMode = Boolean(anchorEl);

      // Menu UI States
      const menuX = menuEvent?.clientX ?? undefined;
      const menuY = menuEvent?.clientY ?? undefined;
      const hasXY = Boolean(menuX && menuY);
      const isOpen = isButtonMode ? Boolean(anchorEl) : hasXY;
      const anchorPos: MenuProps['anchorPosition'] =
        menuX && menuY ? { top: menuY, left: menuX } : undefined;

      // Notifier does not support multi-select; actions always target the active row.
      const messageItem = contextMenuItem as BroadcastMessageItem | null;
      const messageId = messageItem?.message_id;
      const isReadOnlyStatus = READ_ONLY_STATUSES.has(messageItem?.send_status ?? '');

      // --------------------------------------------------
      // Event Handlers, Styles
      // --------------------------------------------------
      const iconSx = getMiniMenuIconSx(theme);
      const menuListRef = useRef<HTMLUListElement>(null);

      const internalOnClose = useCallback(
        (event?: MuiCloseEvent, reason?: MuiCloseReasons) => {
          const maybeEvent = event as
            | {
                stopPropagation?: () => void;
                preventDefault?: () => void;
              }
            | undefined;
          maybeEvent?.stopPropagation?.();
          maybeEvent?.preventDefault?.();
          handleMiniMenuClose();
        },
        [handleMiniMenuClose],
      );

      const handleMenuClickCaptures = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
          if (!menuListRef?.current?.contains(event?.target as Node)) {
            event?.stopPropagation?.();
            event?.preventDefault?.();
            internalOnClose(event, 'backdropClick');
          }
        },
        [internalOnClose, menuListRef],
      );

      // --------------------------------------------------
      // Edit / View Button
      // --------------------------------------------------
      const openMessageState: ButtonState = useMemo(() => {
        const showEdit = Boolean(menuConfig?.edit?.enabled);
        const canOpen = Boolean(messageId);

        if (!canOpen) {
          return {
            disabled: true,
            show: showEdit,
          };
        }

        return {
          disabled: false,
          show: showEdit,
        };
      }, [menuConfig?.edit?.enabled, messageId]);

      const handleOpenMessage = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
          event?.preventDefault();
          event?.stopPropagation();
          if (openMessageState?.disabled || !messageId) {
            return;
          }

          const next = new URLSearchParams(searchParams);
          next.set('openModal', messageId);
          setSearchParams(next, { replace: false });
          internalOnClose();
        },
        [
          internalOnClose,
          messageId,
          openMessageState?.disabled,
          searchParams,
          setSearchParams,
        ],
      );

      // --------------------------------------------------
      // View Logs Button
      // --------------------------------------------------
      const viewLogsState: ButtonState = useMemo(() => {
        const showViewLogs = Boolean(menuConfig?.viewLogs?.enabled) && isStaffUser;
        const canViewLogs =
          Boolean(messageId) && LOGGABLE_STATUSES.has(messageItem?.send_status ?? '');

        if (!canViewLogs) {
          return {
            disabled: true,
            show: showViewLogs,
          };
        }

        return {
          disabled: false,
          show: showViewLogs,
        };
      }, [
        messageItem?.send_status,
        menuConfig?.viewLogs?.enabled,
        messageId,
        isStaffUser,
      ]);

      const handleViewLogs = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
          event?.preventDefault();
          event?.stopPropagation();
          if (viewLogsState?.disabled || !messageId) {
            return;
          }

          const next = new URLSearchParams(searchParams);
          next.set('viewLogs', messageId);
          setSearchParams(next, { replace: false });
          internalOnClose();
        },
        [
          internalOnClose,
          messageId,
          searchParams,
          setSearchParams,
          viewLogsState?.disabled,
        ],
      );

      // --------------------------------------------------
      // Render
      // --------------------------------------------------
      return (
        <Menu
          ref={ref}
          id="notifier-minimenu"
          data-testid="notifier-minimenu"
          disableEnforceFocus={true}
          open={isOpen}
          onClose={internalOnClose}
          sx={miniMenuSx}
          hideBackdrop={true}
          onMouseDownCapture={handleMenuClickCaptures}
          onContextMenuCapture={handleMenuClickCaptures}
          {...(isButtonMode
            ? { anchorEl }
            : {
                anchorReference: 'anchorPosition' as const,
                anchorPosition: anchorPos,
                TransitionProps: { timeout: { enter: 0, exit: 0 } },
              })}
          slotProps={{
            list: {
              autoFocusItem: true,
              ref: menuListRef,
            },
          }}
        >
          {/* Edit / View */}
          {openMessageState.show && (
            <Tooltip
              title={openMessageState?.info || ''}
              placement="left"
              arrow
              disableHoverListener={!openMessageState.disabled || !openMessageState.info}
            >
              <span>
                <MenuItem
                  data-testid="notifier-edit-view-menu-item"
                  onClick={handleOpenMessage}
                  disabled={openMessageState.disabled}
                >
                  <ListItemIcon>
                    {isReadOnlyStatus ? (
                      <VisibilityIcon sx={iconSx} />
                    ) : (
                      <EditIcon sx={iconSx} />
                    )}
                  </ListItemIcon>
                  <ListItemText>
                    {isReadOnlyStatus
                      ? t('blox.notifier.mini_menu.view', 'View')
                      : t('blox.notifier.mini_menu.edit', 'Edit')}
                  </ListItemText>
                </MenuItem>
              </span>
            </Tooltip>
          )}

          {/* View Logs */}
          {viewLogsState.show && (
            <Tooltip
              title={viewLogsState?.info || ''}
              placement="left"
              arrow
              disableHoverListener={!viewLogsState.disabled || !viewLogsState.info}
            >
              <span>
                <MenuItem
                  data-testid="notifier-view-logs-menu-item"
                  onClick={handleViewLogs}
                  disabled={viewLogsState.disabled}
                >
                  <ListItemIcon>
                    <ReceiptLongIcon sx={iconSx} />
                  </ListItemIcon>
                  <ListItemText>{t('blox.notifier.logs.action')}</ListItemText>
                </MenuItem>
              </span>
            </Tooltip>
          )}
        </Menu>
      );
    },
  ),
);

NotifierMiniMenu.displayName = 'NotifierMiniMenu';
